import { useUserContext } from "../../Hooks/useUserContext";
import { useUserService } from "../../Hooks/useUserService";
import { RouterLink } from "../../Shared/RouterLink";
import { AuthLoadingScreen } from "../../Shared/AuthLoadingScreen";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Button,
	IconButton,
	Tooltip,
	Paper,
	Checkbox
} from "@material-ui/core";
import { Spinner } from "../../Shared/Spinner";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";

export function UserAdmin(props) {
	const user = useUserContext(props.config);
	const userService = useUserService(props.config);
	const [users, setUsers] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [deletingUserId, setDeletingUserId] = useState(null);

	const sortedUsers = [...users].sort((a, b) => {
		const aName = (a?.username ?? "").toString().toLocaleLowerCase();
		const bName = (b?.username ?? "").toString().toLocaleLowerCase();
		if (aName < bName) return -1;
		if (aName > bName) return 1;
		return 0;
	});

	const isUserAdmin = !!user?.isUserAdmin;

	useEffect(() => {
		document.title = "User Admin - " + props.config.appName;
		if (!user.isInitialized || !isUserAdmin) {
			setUsers([]);
			setIsLoading(false);
			return;
		}

		loadUsers();
	}, [user.isInitialized, isUserAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

	function loadUsers() {
		setIsLoading(true);
		userService.getAllUsers(
			(response) => {
				if (response && response.status === 200 && response.data) {
					setUsers(response.data);
				}
				setIsLoading(false);
			},
			(error) => {
				console.error(error);
				alert("Failed to load users");
				setIsLoading(false);
			}
		);
	}

	function handleDelete(event, userToDelete) {
		if (event && event.preventDefault)
			event.preventDefault();

		if (!window.confirm(`Delete ${userToDelete.username}?`))
			return;

		setDeletingUserId(userToDelete.appUserId);
		userService.deleteUser(userToDelete.appUserId, () => {
			loadUsers();
			setDeletingUserId(null);
		}, (error) => {
			console.error(error);
			alert("Failed to delete user");
			setDeletingUserId(null);
		});
	}

	// Show loading screen while checking authentication
	if (!user.isInitialized) {
		return <AuthLoadingScreen />;
	}

	if (!user || !user.isUserAdmin)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have user admin permissions to view this page!
					</Typography>
					<Typography style={{ paddingLeft: 40 }} variant="subtitle1">
						Logged in: {user.isLoggedIn ? "True" : "False"}
					</Typography>
					<Typography style={{ paddingTop: 20 }} variant="subtitle1">
						<RouterLink to="/login" >
							{user.isLoggedIn ? "" : "Login Now"}
						</RouterLink>
					</Typography>
				</Grid>
			</Grid>
		);

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					User Administration
				</Typography>
			</Grid>
			<Grid item xs={12} style={{ marginBottom: 12 }}>
				<Button
					variant="contained"
					color="primary"
					component={RouterLink}
					to="/admin/users/create"
				>
					<AddIcon style={{ marginRight: 10 }} />
					Add User
				</Button>
			</Grid>
			<Grid item xs={13}>{/* add +1 to this xs value to fix a display issue */}
				<Paper>
					{isLoading ? (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
							<Spinner size={24} style={{ marginRight: 10 }} />
							<Typography>Loading...</Typography>
						</div>
					) : (
						<Table className="nowrap-table condensed-table">
					<TableHead>
						<TableRow className="user-admin-header">
							<TableCell style={{ width: 1, padding: 4 }}></TableCell>
							<TableCell>Username</TableCell>
							<TableCell>First Name</TableCell>
							<TableCell>Last Name</TableCell>
							<TableCell align="center">View Recipe</TableCell>
							<TableCell align="center">Edit Recipe</TableCell>
							<TableCell align="center">Send To Kitchen</TableCell>
							<TableCell align="center">User Admin</TableCell>
							<TableCell align="center">Volume To Mass Admin</TableCell>
							<TableCell align="center">Unit Admin</TableCell>
							<TableCell align="center">Unit Approp Admin</TableCell>
							<TableCell align="center">Metric Conv Admin</TableCell>
							<TableCell align="center">Tag Admin</TableCell>
							<TableCell align="center">Last Login</TableCell>
						</TableRow>
					</TableHead>
							<TableBody>
								{sortedUsers.map((u) => (
									<TableRow key={u.appUserId}>
										<TableCell align="left" style={{ width: 1, padding: 4 }}>
											<div style={{ display: "flex", gap: 4 }}>
												<Tooltip title="Edit">
													<IconButton
														color="primary"
														size="small"
														className="rb-icon-btn"
														component={RouterLink}
														to={`/admin/users/${u.appUserId}/edit`}
														aria-label="Edit user"
													>
														<EditIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton
														color="primary"
														size="small"
														className="rb-icon-btn"
														component={RouterLink}
														to={`/admin/users/${u.appUserId}/edit`}
														onClick={(e) => handleDelete(e, u)}
														disabled={deletingUserId === u.appUserId}
														aria-label="Delete user"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										</TableCell>
										<TableCell>{u.username}</TableCell>
										<TableCell>{u.firstName}</TableCell>
										<TableCell>{u.lastName}</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.canViewRecipe} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.canEditRecipe} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.canSendToKitchen} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.isUserAdmin} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.isVolumeToMassAdmin} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.isUnitAdmin} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.isUnitAppropriationAdmin} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.isMetricConversionAdmin} disabled />
										</TableCell>
										<TableCell align="center">
											<Checkbox checked={u.isTagAdmin} disabled />
										</TableCell>
										<TableCell align="center">
											{u.lastLoggedInDate ? new Date(u.lastLoggedInDate).toISOString().substring(0, 10) : "Never"}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</Grid>
		</Grid>
	);
}
